import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { toast } from "sonner";
import { StudyDetail } from "./StudyDetail";
import { Collaborations } from "./Collaborations";
import { useSearchParams } from "react-router-dom";

const MODALITIES = ["", "MG", "CR", "CT", "MR", "US", "DX", "XA", "NM", "PT", "OT"];
const STATUS_STYLE = {
  needs_association: "bg-amber-100 text-amber-800",
  matched: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
};

export function DiagnosticArchive() {
  const { t } = useTranslation();
  const [data, setData] = useState({ studies: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [f, setF] = useState({ q: "", modality: "", matching_status: "", date_from: "", date_to: "", agent_id: "", sort: "received_desc", page: 1 });
  const [archives, setArchives] = useState([]);
  const [archiveOwner, setArchiveOwner] = useState("");
  const [showCollab, setShowCollab] = useState(false);

  const loadArchives = useCallback(() => api.get("/collaborations/archives").then(({ data }) => setArchives(data.archives || [])).catch(() => {}), []);
  useEffect(() => { loadArchives(); }, [loadArchives]);
  const [archiveSp] = useSearchParams();
  useEffect(() => { if (archiveSp.get("collab")) setShowCollab(true); }, [archiveSp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: f.sort, page: f.page, page_size: 25 };
      if (f.q) params.q = f.q;
      if (f.modality) params.modality = f.modality;
      if (f.matching_status) params.matching_status = f.matching_status;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.agent_id) params.agent_id = f.agent_id;
      if (archiveOwner) params.archive_owner = archiveOwner;
      const { data } = await api.get("/pam-connect/studies", { params });
      setData(data);
    } catch { toast.error(t("diag.load_err")); }
    finally { setLoading(false); }
  }, [f, archiveOwner]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => { api.get("/pam-connect/agents").then(({ data }) => setConnectors(data.agents || [])).catch(() => {}); }, []);

  const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");
  const statusLabel = (s) => t(`diag.st_${s}`);
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (<div data-testid="diagnostic-archive">
    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{t("diag.title")}</h2>
        <p className="text-sm text-slate-500">{t("diag.subtitle")}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {archives.length > 1 && <select className="yp-input !py-1.5 text-sm" value={archiveOwner} onChange={(e) => { setArchiveOwner(e.target.value); setF({ ...f, page: 1 }); }} data-testid="archive-selector" title={t("diag.co_archive")}>
          {archives.map((a) => <option key={a.owner_account_id} value={a.own ? "" : a.owner_account_id}>{a.own ? t("diag.co_my") : a.owner_name}</option>)}
        </select>}
        <button className="yp-btn yp-btn-outline !py-1.5 !px-3 text-sm" onClick={() => setShowCollab(true)} data-testid="collab-open-btn">{t("diag.co_collabs")}</button>
        <span className="text-xs text-slate-400" data-testid="diag-total">{t("diag.count", { n: data.total })}</span>
      </div>
    </div>
    {archiveOwner && <div className="mb-3 text-sm px-3 py-2 rounded-lg bg-blue-50 text-blue-800 border border-blue-100" data-testid="archive-context-banner">{t("diag.co_archive_of", { name: (archives.find((a) => a.owner_account_id === archiveOwner) || {}).owner_name || "" })}</div>}

    {/* Filtri */}
    <div className="yp-card p-3 mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      <input className="yp-input !py-1.5 text-sm col-span-2 sm:col-span-1" placeholder={t("diag.f_search")} value={f.q}
        onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} data-testid="diag-filter-q" />
      <select className="yp-input !py-1.5 text-sm" value={f.modality} onChange={(e) => setF({ ...f, modality: e.target.value, page: 1 })} data-testid="diag-filter-modality">
        <option value="">{t("diag.f_modality")}</option>
        {MODALITIES.filter(Boolean).map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select className="yp-input !py-1.5 text-sm" value={f.matching_status} onChange={(e) => setF({ ...f, matching_status: e.target.value, page: 1 })} data-testid="diag-filter-status">
        <option value="">{t("diag.f_status")}</option>
        <option value="needs_association">{t("diag.st_needs_association")}</option>
        <option value="matched">{t("diag.st_matched")}</option>
        <option value="delivered">{t("diag.st_delivered")}</option>
      </select>
      <select className="yp-input !py-1.5 text-sm" value={f.agent_id} onChange={(e) => setF({ ...f, agent_id: e.target.value, page: 1 })} data-testid="diag-filter-connector">
        <option value="">{t("diag.f_connector")}</option>
        {connectors.map((c) => <option key={c.agent_id} value={c.agent_id}>{c.display_name || c.hostname || c.agent_id}</option>)}
      </select>
      <input className="yp-input !py-1.5 text-sm" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value, page: 1 })} data-testid="diag-filter-from" />
      <input className="yp-input !py-1.5 text-sm" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value, page: 1 })} data-testid="diag-filter-to" />
      <select className="yp-input !py-1.5 text-sm" value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} data-testid="diag-filter-sort">
        <option value="received_desc">{t("diag.sort_date_desc")}</option>
        <option value="received_asc">{t("diag.sort_date_asc")}</option>
        <option value="patient_asc">{t("diag.sort_patient")}</option>
        <option value="modality_asc">{t("diag.sort_modality")}</option>
      </select>
    </div>

    {/* Tabella studi */}
    <div className="yp-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="diag-table">
        <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
          <th className="px-3 py-2">{t("diag.c_patient")}</th>
          <th className="px-3 py-2">{t("diag.c_status")}</th>
          <th className="px-3 py-2 hidden lg:table-cell">{t("diag.c_dicom_id")}</th>
          <th className="px-3 py-2 hidden lg:table-cell">{t("diag.c_accession")}</th>
          <th className="px-3 py-2">{t("diag.c_modality")}</th>
          <th className="px-3 py-2 hidden sm:table-cell">{t("diag.c_date")}</th>
          <th className="px-3 py-2 hidden lg:table-cell">{t("diag.c_device")}</th>
          <th className="px-3 py-2 hidden xl:table-cell">{t("diag.c_connector")}</th>
          <th className="px-3 py-2 text-center">{t("diag.c_images")}</th>
          <th className="px-3 py-2 text-center hidden sm:table-cell">{t("diag.c_series")}</th>
          <th className="px-3 py-2 text-center hidden lg:table-cell">{t("diag.c_docs")}</th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400" data-testid="diag-loading">{t("diag.loading")}</td></tr>}
          {!loading && data.studies.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400" data-testid="diag-empty">{t("diag.empty")}</td></tr>}
          {!loading && data.studies.map((s) => (
            <tr key={s.ingest_id} className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer" onClick={() => setSel(s.ingest_id)} data-testid="diag-row">
              <td className="px-3 py-2 font-medium" data-testid="diag-row-patient">{s.patient_name || <span className="text-slate-400 italic">{t("diag.unknown_patient")}</span>}</td>
              <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[s.status] || "bg-slate-100"}`} data-testid="diag-row-status">{statusLabel(s.status)}</span></td>
              <td className="px-3 py-2 hidden lg:table-cell text-slate-500">{s.source_patient_id || "—"}</td>
              <td className="px-3 py-2 hidden lg:table-cell text-slate-500">{s.accession_number || "—"}</td>
              <td className="px-3 py-2">{s.modality || "—"}</td>
              <td className="px-3 py-2 hidden sm:table-cell text-slate-500">{fmtDate(s.study_date)}</td>
              <td className="px-3 py-2 hidden lg:table-cell text-slate-500">{s.device_label || "—"}</td>
              <td className="px-3 py-2 hidden xl:table-cell text-slate-500">{s.connector_label || "—"}</td>
              <td className="px-3 py-2 text-center">{s.instance_count}</td>
              <td className="px-3 py-2 text-center hidden sm:table-cell">{s.series_count}</td>
              <td className="px-3 py-2 text-center hidden lg:table-cell">{s.documents_count}{s.report_published ? " ✓" : ""}</td>
            </tr>))}
        </tbody>
      </table>
    </div>

    {pages > 1 && <div className="flex items-center justify-center gap-2 mt-3 text-sm">
      <button className="yp-btn yp-btn-ghost !py-1 !px-3" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="diag-prev">‹</button>
      <span className="text-slate-500" data-testid="diag-page">{f.page} / {pages}</span>
      <button className="yp-btn yp-btn-ghost !py-1 !px-3" disabled={f.page >= pages} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="diag-next">›</button>
    </div>}

    {sel && <StudyDetail ingestId={sel} onClose={() => setSel(null)} onChanged={load} />}
    {showCollab && <Collaborations onClose={() => setShowCollab(false)} onChanged={loadArchives} />}
  </div>);
}
